import {
  AdjustmentsHorizontalIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  CameraIcon,
  CheckCircleIcon,
  CurrencyEuroIcon,
  ExclamationTriangleIcon,
  FireIcon,
  HomeIcon,
  HomeModernIcon,
  LightBulbIcon,
  MinusIcon,
  PhotoIcon,
  PlusIcon,
  SparklesIcon,
  StarIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useImperativeHandle, useRef, useState } from 'react';

import type { PriceSuggestionBucket, PriceSuggestions, ValuationPhoto, ValuationPhotoLocal, ValuationProperty, ValuationRooms } from '../../../@types/Valuation';

export interface ValuationPropertyFormHandle {
  validate: () => boolean;
}

interface Props {
  property: ValuationProperty;
  onChange: (property: ValuationProperty) => void;
  accentColor: string;
  priceSuggestions: PriceSuggestions | null;
}

interface SuggestionCardConfig {
  key: 'copro' | 'apartments' | 'houses';
  label: string;
  subtitle: string;
}

const SUGGESTION_CARDS: SuggestionCardConfig[] = [
  { key: 'copro', label: 'Copropriété', subtitle: 'Ventes dans la copropriété' },
  { key: 'apartments', label: 'Appartements', subtitle: 'Dans un rayon de 150 m' },
  { key: 'houses', label: 'Maisons', subtitle: 'Dans un rayon de 150 m' },
];

const computeSuggestionRange = (avg: number, area: number): { min: number; max: number } => ({
  min: Math.round((avg * area * 0.95) / 5000) * 5000,
  max: Math.round((avg * area * 1.05) / 5000) * 5000,
});

const formatNumber = (value: number): string => new Intl.NumberFormat('fr-FR').format(value);

const DPE_OPTIONS: Array<{ key: NonNullable<ValuationProperty['dpe']>; color: string; label: string }> = [
  { key: 'A', color: 'bg-green-500', label: 'Excellent' },
  { key: 'B', color: 'bg-green-400', label: 'Très bon' },
  { key: 'C', color: 'bg-lime-400', label: 'Bon' },
  { key: 'D', color: 'bg-yellow-400', label: 'Moyen' },
  { key: 'E', color: 'bg-orange-400', label: 'Faible' },
  { key: 'F', color: 'bg-orange-500', label: 'Très faible' },
  { key: 'G', color: 'bg-red-500', label: 'Critique' },
];

const HEATING_OPTIONS = ['Aucun', 'Électrique', 'Gaz individuel', 'Gaz collectif', 'Fioul', 'Pompe à chaleur', 'Bois', 'Solaire'];

interface RoomConfig {
  key: keyof ValuationRooms;
  label: string;
  emoji: React.ReactNode;
  description: string;
}

const ROOM_CONFIGS: RoomConfig[] = [
  { key: 'bedroom', label: 'Chambres', emoji: <span role="img" aria-label="bed">🛏️</span>, description: 'Chambres à coucher' },
  { key: 'livingroom', label: 'Salon', emoji: <span role="img" aria-label="couch">🛋️</span>, description: 'Salon / Séjour' },
  { key: 'kitchen', label: 'Cuisine', emoji: <span role="img" aria-label="cooking">🍳</span>, description: 'Cuisine équipée' },
  { key: 'bathroom', label: 'Salles de bain', emoji: <span role="img" aria-label="shower">🚿</span>, description: 'Salles de bain' },
  { key: 'wc', label: 'Toilettes', emoji: <span role="img" aria-label="toilet">🚽</span>, description: 'WC séparés' },
  { key: 'garage', label: 'Garage', emoji: <span role="img" aria-label="car">🚗</span>, description: 'Places de garage' },
];

const getPhotoSrc = (photo: ValuationPhoto): string =>
  photo.source === 'local' ? photo.preview : photo.url;

const ValuationPropertyForm = React.forwardRef<ValuationPropertyFormHandle, Props>(({ property, onChange, accentColor, priceSuggestions }, ref) => {
  const [strengthInput, setStrengthInput] = useState('');
  const [weaknessInput, setWeaknessInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState<'specs' | 'rooms' | 'photos'>('specs');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const update = (patch: Partial<ValuationProperty>) => {
    onChange({ ...property, ...patch });
  };

  const updateRoom = (key: keyof ValuationRooms, delta: number) => {
    update({
      roomsDetail: {
        ...property.roomsDetail,
        [key]: Math.max(0, Math.min(10, property.roomsDetail[key] + delta)),
      },
    });
  };

  const addStrength = () => {
    const v = strengthInput.trim();
    if (v && !property.strengths.includes(v)) {
      update({ strengths: [...property.strengths, v] });
      setStrengthInput('');
    }
  };

  const addWeakness = () => {
    const v = weaknessInput.trim();
    if (v && !property.weaknesses.includes(v)) {
      update({ weaknesses: [...property.weaknesses, v] });
      setWeaknessInput('');
    }
  };

  const handlePhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) { return; }
    const allowed = ['image/jpeg', 'image/png', 'image/jpg'];
    const newPhotos: ValuationPhotoLocal[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (allowed.includes(file.type) && file.size <= 5 * 1024 * 1024) {
        newPhotos.push({ source: 'local', file, preview: URL.createObjectURL(file) });
      }
    }
    if (newPhotos.length > 0) {
      update({ photos: [...property.photos, ...newPhotos] });
    }
    e.target.value = '';
  };

  const removePhoto = (idx: number) => {
    const photo = property.photos[idx];
    if (photo.source === 'local') { URL.revokeObjectURL(photo.preview); }
    update({ photos: property.photos.filter((_, i) => i !== idx) });
  };

  const setMainPhoto = (idx: number) => {
    if (idx === 0) { return; }
    const photos = [...property.photos];
    const [photo] = photos.splice(idx, 1);
    photos.unshift(photo);
    update({ photos });
  };

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!property.type) { errs.type = 'Le type de bien est requis.'; }
    if (!property.rooms || property.rooms < 1) { errs.rooms = 'Le nombre de pièces est requis.'; }
    if (!property.price || property.price < 1) { errs.price = 'Le prix minimum est requis.'; }
    if (!property.priceMax || property.priceMax < 1) { errs.priceMax = 'Le prix maximum est requis.'; }
    if (property.price && property.priceMax && property.priceMax < property.price) {
      errs.priceMax = 'Le prix maximum doit être supérieur au prix minimum.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [property]);

  useImperativeHandle(ref, () => ({ validate }), [validate]);

  const pricePerSqm = property.price && property.area ? Math.round(property.price / property.area) : 0;
  const priceMaxPerSqm = property.priceMax && property.area ? Math.round(property.priceMax / property.area) : 0;

  const applyBucketSuggestion = (bucket: PriceSuggestionBucket) => {
    if (!property.area || bucket.salesAvgForM2 <= 0) { return; }
    const range = computeSuggestionRange(bucket.salesAvgForM2, property.area);
    update({ price: range.min, priceMax: range.max });
  };

  const hasAnySuggestion = !!priceSuggestions && SUGGESTION_CARDS.some(({ key }) => {
    const bucket = priceSuggestions[key];
    return bucket.salesAvgForM2 > 0 && bucket.nbSales > 0;
  });

  const totalRooms = Object.values(property.roomsDetail).reduce((sum, v) => sum + v, 0);
  const roomsFilled = totalRooms > 0;
  const photosFilled = property.photos.length > 0;

  const tabs = [
    { id: 'specs' as const, label: 'Spécifications', Icon: AdjustmentsHorizontalIcon, filled: true, badge: null },
    { id: 'rooms' as const, label: 'Pièces', Icon: HomeModernIcon, filled: roomsFilled, badge: roomsFilled ? String(totalRooms) : null },
    { id: 'photos' as const, label: 'Photos', Icon: CameraIcon, filled: photosFilled, badge: photosFilled ? String(property.photos.length) : null },
  ];

  return (
    <div className="space-y-4 px-1">
      {/* Tab navigation */}
      <div className="-mx-1 flex gap-1.5 rounded-xl bg-slate-100 p-1.5">
        {tabs.map((tab) => {
          const isActive = activeSection === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              className={`relative flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${isActive ? 'text-white shadow-sm' : 'text-slate-500 hover:bg-slate-200 hover:text-slate-700'}`}
              style={isActive ? { backgroundColor: accentColor } : undefined}
              onClick={() => setActiveSection(tab.id)}
            >
              {tab.filled && !isActive && (
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
              )}
              {!tab.filled && !isActive && (
                <tab.Icon className="h-4 w-4 text-slate-400" />
              )}
              {isActive && (
                <tab.Icon className="h-4 w-4" />
              )}
              <span>{tab.label}</span>
              {tab.badge && (
                <span className={`ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold ${isActive ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-600'}`}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* === SPECS TAB === */}
      {activeSection === 'specs' && (
        <div className="space-y-5 pb-6">
          {/* Property type */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Type de bien <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-3">
              {([
                { id: 'apartment' as const, label: 'Appartement', Icon: BuildingOffice2Icon },
                { id: 'house' as const, label: 'Maison', Icon: HomeIcon },
              ]).map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`relative flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all ${property.type === id ? 'scale-[1.02] shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}`}
                  style={property.type === id ? { borderColor: accentColor, backgroundColor: `${accentColor  }10` } : undefined}
                  onClick={() => update({ type: id })}
                >
                  <Icon className="h-7 w-7" style={{ color: property.type === id ? accentColor : '#94a3b8' }} />
                  <span className={`text-sm font-medium ${property.type === id ? '' : 'text-slate-600'}`} style={property.type === id ? { color: accentColor } : undefined}>{label}</span>
                  {property.type === id && <CheckCircleIcon className="absolute top-2 right-2 h-5 w-5" style={{ color: accentColor }} />}
                </button>
              ))}
            </div>
            {errors.type && <p className="mt-1 text-xs text-red-500">{errors.type}</p>}
          </div>

          {/* Basic info grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Nombre de pièces <span className="text-red-500">*</span></label>
              <div className="flex items-center gap-2">
                <button type="button" className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 hover:bg-slate-100" onClick={() => update({ rooms: Math.max(1, property.rooms - 1) })}>
                  <MinusIcon className="h-4 w-4" />
                </button>
                <input type="number" min={1} value={property.rooms} onChange={(e) => update({ rooms: Math.max(1, parseInt(e.target.value, 10) || 1) })} className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 text-center text-sm focus:border-transparent focus:ring-2" style={{ ['--tw-ring-color' as string]: accentColor }} />
                <button type="button" className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 hover:bg-slate-100" onClick={() => update({ rooms: property.rooms + 1 })}>
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
              {errors.rooms && <p className="mt-1 text-xs text-red-500">{errors.rooms}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Surface (m²)</label>
              <div className="relative">
                <input type="number" min={0} value={property.area ?? ''} onChange={(e) => update({ area: e.target.value ? parseInt(e.target.value, 10) : undefined })} placeholder="65" className="w-full rounded-lg border border-slate-300 px-3 py-1.5 pr-10 text-sm" />
                <span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs text-slate-400">m²</span>
              </div>
            </div>
          </div>

          {/* Year + Heating */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 flex items-center text-sm font-medium text-slate-700"><CalendarIcon className="mr-1.5 h-4 w-4 text-slate-400" />Année</label>
              <input type="text" value={property.year ?? ''} onChange={(e) => update({ year: e.target.value || undefined })} placeholder="1985" maxLength={4} className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="mb-1 flex items-center text-sm font-medium text-slate-700"><FireIcon className="mr-1.5 h-4 w-4 text-slate-400" />Chauffage</label>
              <select value={property.heating ?? ''} onChange={(e) => update({ heating: e.target.value || undefined })} className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm">
                <option value="">Non renseigné</option>
                {HEATING_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          </div>

          {/* DPE */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">DPE</label>
            <div className="flex gap-1.5">
              {DPE_OPTIONS.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  className={`relative flex-1 rounded-lg border-2 py-2 text-center text-sm font-bold transition-all ${property.dpe === d.key ? 'scale-110 text-white shadow-md' : 'border-transparent text-white opacity-70 hover:scale-105 hover:opacity-100'} ${d.color}`}
                  style={property.dpe === d.key ? { borderColor: '#1e293b' } : undefined}
                  onClick={() => update({ dpe: property.dpe === d.key ? undefined : d.key })}
                >
                  {d.key}
                  {property.dpe === d.key && <CheckCircleIcon className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-slate-800 text-white" />}
                </button>
              ))}
            </div>
            {property.dpe && <p className="mt-1 text-center text-xs text-slate-500">{DPE_OPTIONS.find((d) => d.key === property.dpe)?.label}</p>}
          </div>

          {/* Price range */}
          <div>
            <label className="mb-2 flex items-center text-sm font-semibold text-slate-800"><CurrencyEuroIcon className="mr-1.5 h-4 w-4" />Fourchette de prix <span className="ml-1 text-red-500">*</span></label>

            {hasAnySuggestion && priceSuggestions && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="mb-2 flex items-center gap-1.5">
                  <LightBulbIcon className="h-4 w-4 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-800">Suggestion de prix</span>
                  {!property.area && (
                    <span className="ml-auto text-[10px] font-medium text-amber-700">Renseignez la surface pour pré-remplir</span>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {SUGGESTION_CARDS.map((card) => {
                    const bucket = priceSuggestions[card.key];
                    const avg = Math.round(bucket.salesAvgForM2);
                    const hasData = bucket.salesAvgForM2 > 0 && bucket.nbSales > 0;
                    const canApply = hasData && !!property.area;
                    const range = canApply ? computeSuggestionRange(bucket.salesAvgForM2, property.area!) : null;
                    return (
                      <div
                        key={card.key}
                        className={`flex flex-col rounded-md border border-amber-200 bg-white p-2.5 ${hasData ? '' : 'opacity-60'}`}
                      >
                        <div className="text-[11px] font-semibold text-amber-900">{card.label}</div>
                        <div className="text-[10px] text-amber-700">{card.subtitle}</div>
                        {hasData ? (
                          <>
                            <div className="mt-1.5 text-sm font-bold text-slate-800">
                              {formatNumber(avg)} <span className="text-xs font-medium text-slate-500">€/m²</span>
                            </div>
                            <div className="text-[10px] text-slate-400">
                              {bucket.nbSales} vente{bucket.nbSales > 1 ? 's' : ''} (5 ans)
                            </div>
                            {range && (
                              <div className="mt-1 text-[10px] text-slate-500">
                                {formatNumber(range.min)} € – {formatNumber(range.max)} €
                              </div>
                            )}
                            <button
                              type="button"
                              disabled={!canApply}
                              className="mt-2 rounded-md px-2 py-1 text-[11px] font-medium text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                              style={{ backgroundColor: accentColor }}
                              onClick={() => applyBucketSuggestion(bucket)}
                            >
                              Utiliser
                            </button>
                          </>
                        ) : (
                          <div className="mt-2 text-[10px] text-slate-400 italic">Pas assez de données</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Prix minimum</label>
                <div className="relative">
                  <input type="number" min={0} value={property.price || ''} onChange={(e) => update({ price: parseInt(e.target.value, 10) || 0 })} placeholder="180 000" className={`w-full rounded-lg border px-3 py-1.5 pr-6 text-sm ${errors.price ? 'border-red-300' : 'border-slate-300'}`} />
                  <span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs text-slate-400">€</span>
                </div>
                {pricePerSqm > 0 && <p className="mt-0.5 text-xs text-slate-400">{new Intl.NumberFormat('fr-FR').format(pricePerSqm)} €/m²</p>}
                {errors.price && <p className="mt-0.5 text-xs text-red-500">{errors.price}</p>}
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Prix maximum</label>
                <div className="relative">
                  <input type="number" min={0} value={property.priceMax || ''} onChange={(e) => update({ priceMax: parseInt(e.target.value, 10) || 0 })} placeholder="220 000" className={`w-full rounded-lg border px-3 py-1.5 pr-6 text-sm ${errors.priceMax ? 'border-red-300' : 'border-slate-300'}`} />
                  <span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs text-slate-400">€</span>
                </div>
                {priceMaxPerSqm > 0 && <p className="mt-0.5 text-xs text-slate-400">{new Intl.NumberFormat('fr-FR').format(priceMaxPerSqm)} €/m²</p>}
                {errors.priceMax && <p className="mt-0.5 text-xs text-red-500">{errors.priceMax}</p>}
              </div>
            </div>
          </div>

          {/* Seller + Description */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="mb-1 flex items-center text-sm font-medium text-slate-700"><UserIcon className="mr-1.5 h-4 w-4 text-slate-400" />Nom du vendeur</label>
              <input type="text" value={property.seller} onChange={(e) => update({ seller: e.target.value })} placeholder="M. Dupont" maxLength={64} className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Description du bien</label>
              <textarea value={property.description} onChange={(e) => update({ description: e.target.value })} placeholder="Bel appartement lumineux, proche des commodités..." rows={3} className="w-full resize-none rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
            </div>
          </div>

          {/* Strengths + Weaknesses */}
          <div className="mb-8 grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 flex items-center text-sm font-medium text-slate-700"><SparklesIcon className="mr-1 h-4 w-4 text-green-500" />Points forts</label>
              <div className="mb-1.5 flex flex-wrap gap-1">
                {property.strengths.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                    {s}
                    <button type="button" onClick={() => update({ strengths: property.strengths.filter((_, idx) => idx !== i) })}><XMarkIcon className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1.5">
                <input type="text" value={strengthInput} onChange={(e) => setStrengthInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addStrength(); } }} placeholder="Ajouter..." className="flex-1 rounded-lg border border-slate-300 px-3 py-1 text-sm" />
                <button type="button" onClick={addStrength} disabled={!strengthInput.trim()} className="rounded-lg bg-green-600 px-2 py-1 text-white transition hover:bg-green-500 disabled:cursor-not-allowed disabled:bg-green-300"><PlusIcon className="h-4 w-4" /></button>
              </div>
            </div>
            <div>
              <label className="mb-1 flex items-center text-sm font-medium text-slate-700"><ExclamationTriangleIcon className="mr-1 h-4 w-4 text-red-500" />Points faibles</label>
              <div className="mb-1.5 flex flex-wrap gap-1">
                {property.weaknesses.map((w, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                    {w}
                    <button type="button" onClick={() => update({ weaknesses: property.weaknesses.filter((_, idx) => idx !== i) })}><XMarkIcon className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1.5">
                <input type="text" value={weaknessInput} onChange={(e) => setWeaknessInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addWeakness(); } }} placeholder="Ajouter..." className="flex-1 rounded-lg border border-slate-300 px-3 py-1 text-sm" />
                <button type="button" onClick={addWeakness} disabled={!weaknessInput.trim()} className="rounded-lg bg-red-600 px-2 py-1 text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:bg-red-300"><PlusIcon className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === ROOMS TAB === */}
      {activeSection === 'rooms' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Définissez le nombre de pièces de chaque type.</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {ROOM_CONFIGS.map((cfg) => (
              <div key={cfg.key} className="rounded-xl border border-slate-200 bg-white p-4 transition hover:shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-xl">{cfg.emoji}</span>
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{cfg.label}</div>
                    <div className="text-[10px] text-slate-400">{cfg.description}</div>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <button type="button" disabled={property.roomsDetail[cfg.key] <= 0} className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40" onClick={() => updateRoom(cfg.key, -1)}>
                    <MinusIcon className="h-3.5 w-3.5 text-slate-600" />
                  </button>
                  <span className="w-8 text-center text-xl font-bold text-slate-900">{property.roomsDetail[cfg.key]}</span>
                  <button type="button" disabled={property.roomsDetail[cfg.key] >= 10} className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40" onClick={() => updateRoom(cfg.key, 1)}>
                    <PlusIcon className="h-3.5 w-3.5 text-slate-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {/* Quick presets */}
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" className="rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100" onClick={() => update({ roomsDetail: { bedroom: 3, livingroom: 1, kitchen: 1, bathroom: 1, wc: 1, garage: 1 } })}><span role="img" aria-label="house">🏠</span> Maison type</button>
            <button type="button" className="rounded-lg bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-700 hover:bg-purple-100" onClick={() => update({ roomsDetail: { bedroom: 2, livingroom: 1, kitchen: 1, bathroom: 1, wc: 0, garage: 0 } })}><span role="img" aria-label="office building">🏢</span> Appartement type</button>
            <button type="button" className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100" onClick={() => update({ roomsDetail: { bedroom: 0, livingroom: 0, kitchen: 0, bathroom: 0, wc: 0, garage: 0 } })}><span role="img" aria-label="refresh">🔄</span> Remettre à zéro</button>
          </div>
        </div>
      )}

      {/* === PHOTOS TAB === */}
      {activeSection === 'photos' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Importez les prises de vue du bien. La première photo sera mise en avant dans le rapport.</p>
          {/* Drop zone */}
          <div
            className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-8 transition hover:border-slate-400 hover:bg-slate-100"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files.length > 0) { const dt = new DataTransfer(); for (let i = 0; i < e.dataTransfer.files.length; i++) { dt.items.add(e.dataTransfer.files[i]); } const inp = fileInputRef.current; if (inp) { inp.files = dt.files; inp.dispatchEvent(new Event('change', { bubbles: true })); } } }}
          >
            <PhotoIcon className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-2 text-sm font-medium text-slate-600">Cliquer ou glisser-déposer</p>
            <p className="text-xs text-slate-400">JPG, PNG — 5 Mo max</p>
            <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" multiple className="hidden" onChange={handlePhotoAdd} />
          </div>
          {/* Photo grid */}
          {property.photos.length > 0 && (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {property.photos.map((photo, idx) => (
                <div key={idx} className="group relative overflow-hidden rounded-lg">
                  <img src={getPhotoSrc(photo)} alt="" className="aspect-square w-full object-cover" />
                  <div className="absolute inset-0 flex items-start justify-end gap-1 bg-gradient-to-b from-black/40 to-transparent p-1.5 opacity-0 transition group-hover:opacity-100">
                    {idx === 0 ? (
                      <span className="rounded bg-yellow-500 p-1 text-white" title="Photo principale"><StarIcon className="h-4 w-4" /></span>
                    ) : (
                      <button type="button" onClick={() => setMainPhoto(idx)} className="rounded bg-white/90 p-1 text-yellow-500 hover:bg-yellow-400 hover:text-white" title="Mettre en avant"><StarIcon className="h-4 w-4" /></button>
                    )}
                    <button type="button" onClick={() => removePhoto(idx)} className="rounded bg-red-500 p-1 text-white hover:bg-red-400" title="Supprimer"><TrashIcon className="h-4 w-4" /></button>
                  </div>
                  {idx === 0 && <span className="absolute right-0 bottom-0 left-0 bg-yellow-500/90 py-0.5 text-center text-[10px] font-medium text-white">Photo principale</span>}
                </div>
              ))}
            </div>
          )}
          {property.photos.length === 0 && <p className="py-2 text-center text-sm text-slate-400">Aucune photo importée.</p>}
        </div>
      )}
    </div>
  );
});

ValuationPropertyForm.displayName = 'ValuationPropertyForm';

export default ValuationPropertyForm;
